'use strict';
import has from 'lodash/has';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {buildInjector} from '../../_libs/angular-utils';

// load angular dependencies:
const dependencies = {};
export const injector = buildInjector(dependencies, [
  '$domain',
]);

// AngularDomain -> Redux transition helper methods

/**
 *
 * @param {?Object} instance - POJO or AngularDomain instance
 * @return {?String}
 */
export function getId(instance) {
  return instance ? instance.id || instance.$id : null;
}

/**
 * @param {?Object} instance - POJO or AngularDomain instance
 * @return {?String}
 */
export function getKey(instance) {
  if (has(instance, 'tempId')) {
    return instance.tempId;
  } else {
    return getId(instance);
  }
}

/**
 *
 * @param {?Object} instance - POJO or AngularDomain instance
 * @return {?String}
 */
export function getCollection(instance) {
  if (instance) {
    return instance.$class ? instance.$class.COLLECTION : instance.collection;
  } else {
    return null;
  }
}

/**
 * Get the link prop from the instance.
 * Will only return null if the instance param is null.
 *
 * @param instance
 * @param prop
 * @return {?{id: ?String, collection: ?String}}
 */
export function getLink(instance, prop) {
  if (!isObject(instance)) {
    return null;
  } else if (isFunction(instance.$getLink)) {
    return instance.$getLink(prop);
  } else if (isObject(instance[prop])) {
    return instance[prop];
  } else {
    return {
      id: null,
      collection: null,
    };
  }
}

export function getLinkId(instance, prop) {
  const link = getLink(instance, prop);
  return link ? link.id : null;
}

/**
 *
 * @param {?Object} instance POJO or AngularDomain instance
 * @param {String} prop
 * @return {?Object} AngularDomain class
 */
export function getDomainLink(instance, prop) {
  if (!dependencies.$domain) {
    return Promise.reject(new Error('AppModel has not been initialized properly. If this is a test, you must mock core-utilities/domain/ReduxUtils dependencies.'));
  } else if (!isObject(instance)) {
    return Promise.resolve(null);
  } else if (isFunction(instance.$getDomainLink)) {
    return instance.$getDomainLink(prop);
  } else if (isObject(instance[prop])) {
    return dependencies.$domain.getDomainInstance(instance[prop]);
  } else {
    return Promise.resolve(null);
  }
}

export function getDomainInstance(instance) {
  if (isObject(instance)) {
    if (instance.$class) {
      return Promise.resolve(instance);
    } else if (instance.id && instance.collection) {
      const domainClass = dependencies.$domain.getDomainClass(instance.collection);
      if (domainClass) {
        return domainClass.$get(instance.id);
      } else {
        return Promise.reject(new Error(`$domain Error: could not find a domain class registered for collection ${instance.collection}`));
      }
    }
  } else {
    return Promise.resolve(null);
  }
}

/**
 *
 * @param {?Object} instance - POJO or AngularDomain instance
 * @return {?Object} AngularDomain class
 */
export function getDomainClass(instance) {
  if (!dependencies.$domain) {
    throw new Error('AppModel has not been initialized properly. If this is a test, you must mock core-utilities/domain/ReduxUtils dependencies.');
  } else if (isString(instance)) {
    return dependencies.$domain.getDomainClass(instance);
  } else if (isObject(instance)) {
    return instance.$class ? instance.$class : dependencies.$domain.getDomainClass(instance.collection);
  } else {
    return null;
  }
}

/**
 *
 * @param {?Object} instance - POJO or AngularDomain instance
 * @param {String|Function} [prop="name"] - Property or callback to retreive a string value for the instance
 * @return {?String}
 */
export function getAsString(instance, prop = 'name') {
  if (instance) {
    if (instance.$class) {
      return instance.toString();
    } else if (isString(prop)) {
      return instance[prop];
    } else if (isFunction(prop)) {
      return prop(instance);
    }
  }

  // Default case
  return null;
}

export function getAsLink(instance) {
  return instance ? {
    id: getId(instance),
    collection: getCollection(instance)
  } : null
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/domain/ReduxUtils.js